import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { AlertTriangle, CheckCircle2, Info, ShieldCheck } from 'lucide-react';

const toneConfig = {
  default: {
    eyebrow: 'Decision checkpoint',
    icon: ShieldCheck,
    frame: 'border-blue-100 bg-blue-50 text-blue-700',
    dot: 'bg-blue-500',
    panel: 'border-blue-100 bg-blue-50/45',
    button: 'bg-slate-950 text-white hover:bg-slate-800'
  },
  warning: {
    eyebrow: 'Review before leaving',
    icon: AlertTriangle,
    frame: 'border-amber-200 bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
    panel: 'border-amber-200 bg-amber-50/55',
    button: 'bg-slate-950 text-white hover:bg-slate-800'
  },
  destructive: {
    eyebrow: 'Reversible only by correction',
    icon: AlertTriangle,
    frame: 'border-red-200 bg-red-50 text-red-700',
    dot: 'bg-red-500',
    panel: 'border-red-200 bg-red-50/55',
    button: 'bg-red-600 text-white hover:bg-red-700'
  },
  success: {
    eyebrow: 'Ready to apply',
    icon: CheckCircle2,
    frame: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
    panel: 'border-emerald-200 bg-emerald-50/55',
    button: 'bg-slate-950 text-white hover:bg-slate-800'
  },
  info: {
    eyebrow: 'Workflow update',
    icon: Info,
    frame: 'border-slate-200 bg-slate-50 text-slate-700',
    dot: 'bg-slate-500',
    panel: 'border-slate-200 bg-slate-50',
    button: 'bg-slate-950 text-white hover:bg-slate-800'
  }
};

export default function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  items = [],
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  tone,
  eyebrow,
  summaryLabel = 'What happens next',
  onConfirm
}) {
  const resolvedTone = tone || (destructive ? 'destructive' : 'default');
  const style = toneConfig[resolvedTone] || toneConfig.default;
  const Icon = style.icon;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[560px] overflow-hidden rounded-lg border border-slate-200 bg-white p-0 shadow-2xl">
        <div className="border-b border-slate-200 bg-white px-5 py-5">
          <AlertDialogHeader className="space-y-0 text-left">
            <div className="flex items-start gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${style.frame}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  {eyebrow || style.eyebrow}
                </div>
                <AlertDialogTitle className="mt-1 text-lg font-semibold leading-6 text-slate-950">
                  {title}
                </AlertDialogTitle>
                {description && (
                  <AlertDialogDescription className="mt-2 text-sm leading-6 text-slate-600">
                    {description}
                  </AlertDialogDescription>
                )}
              </div>
            </div>
          </AlertDialogHeader>
        </div>

        {items.length > 0 && (
          <div className="px-5 py-4">
            <div className={`rounded-lg border p-3 text-sm text-slate-700 ${style.panel}`}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="font-semibold text-slate-950">{summaryLabel}</div>
                <div className="rounded-full border border-white/70 bg-white/80 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {items.length} item{items.length === 1 ? '' : 's'}
                </div>
              </div>
              <ul className="space-y-2">
              {items.map((item, index) => (
                <li key={`${item}-${index}`} className="flex gap-2 leading-5">
                  <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`} />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
              </ul>
            </div>
          </div>
        )}

        <AlertDialogFooter className="border-t border-slate-200 bg-slate-50 px-5 py-4 sm:items-center sm:justify-between sm:space-x-0">
          <div className="hidden text-xs text-slate-500 sm:block">
            Changes apply only after you choose the primary action.
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AlertDialogCancel className="mt-0 bg-white">{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={style.button}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
